import { Download, Droplets, Sparkles, Sun, Leaf } from "lucide-react";

export default function CarePage() {
  return (
    <div data-testid="care-page" className="max-w-[1100px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
      <header className="max-w-3xl">
        <div className="eyebrow mb-5">Care</div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
          Looking after <span className="italic">your board</span>.
        </h1>
        <p className="mt-6 text-[#2C2421]/75 leading-relaxed text-lg">
          A little care goes a long way. With a regular wipe and the occasional
          oiling, your board will get better with every year of use.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href="/care-card.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="care-download-btn"
            className="inline-flex items-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-6 py-3 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
          >
            <Download size={14} /> Download care card (PDF)
          </a>
          <a
            href="/care-card.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="care-open-btn"
            className="inline-flex items-center gap-3 border border-[#2C2421] text-[#2C2421] px-6 py-3 text-sm font-medium hover:bg-[#2C2421] hover:text-[#FDFBF7] transition-colors"
          >
            Open in new tab
          </a>
        </div>
      </header>

      <div className="mt-16 grid md:grid-cols-2 gap-10">
        <section className="bg-[#F5F2EB] border border-[#E5E0D8] p-8 md:p-10">
          <Droplets size={22} className="text-[#6F4E37] mb-4" />
          <h2 className="font-serif text-3xl mb-4">Cleaning your board</h2>
          <ul className="space-y-3 text-[#2C2421]/85 leading-relaxed list-disc pl-5">
            <li>Wash in warm soapy water and dry with a tea towel.</li>
            <li>Leave the board standing upright so every surface dries properly.</li>
            <li>Store standing upright in a dry area.</li>
            <li>Do not leave in direct sunlight all day.</li>
            <li>Never put the board in the dishwasher or leave it soaking.</li>
          </ul>
        </section>

        <section className="bg-[#F5F2EB] border border-[#E5E0D8] p-8 md:p-10">
          <Leaf size={22} className="text-[#4A5D4E] mb-4" />
          <h2 className="font-serif text-3xl mb-4">Combating odours</h2>
          <p className="text-[#2C2421]/85 leading-relaxed">
            Occasionally sprinkle salt or baking soda on the board and rub with a
            slice of lemon. This will lift any lingering odours and freshen the
            timber.
          </p>
        </section>

        <section className="bg-[#F5F2EB] border border-[#E5E0D8] p-8 md:p-10 md:col-span-2">
          <Sparkles size={22} className="text-[#6F4E37] mb-4" />
          <h2 className="font-serif text-3xl mb-4">Oiling your board</h2>
          <p className="text-[#2C2421]/85 leading-relaxed">
            Oiling a chopping board is essential for longevity. Soaps and
            detergents dry out the timber and strip its essential oils — the
            very oils that stop the wood from absorbing too much moisture and
            cracking, warping, or growing mould.
          </p>

          <div className="mt-8 grid md:grid-cols-2 gap-8">
            <div>
              <div className="eyebrow mb-2">Daily use</div>
              <p className="text-[#2C2421]/85 leading-relaxed">
                If you use your board often, oil it weekly with the supplied
                mineral oil.
              </p>
            </div>
            <div>
              <div className="eyebrow mb-2">Occasional use</div>
              <p className="text-[#2C2421]/85 leading-relaxed">
                If it lives in the cupboard for special occasions, a monthly oil
                will keep it in prime condition.
              </p>
            </div>
          </div>

          <div className="mt-8 border-t border-[#E5E0D8] pt-6 space-y-4 text-[#2C2421]/85 leading-relaxed">
            <p>
              <strong className="font-medium text-[#2C2421]">Board Butter:</strong>{" "}
              apply after each use with a soft dry cloth, allow it to soak in,
              then remove any excess with a clean dry cloth.
            </p>
            <p>
              <strong className="font-medium text-[#2C2421]">Mineral oil:</strong>{" "}
              after washing and drying the board, apply oil generously with a
              paper towel or small cloth. Leave for around 30 minutes to soak
              in, then buff off any excess with a clean cloth or towel — excess
              oil left on the board will dry and become sticky.
            </p>
          </div>
        </section>
      </div>

      <section className="mt-20 max-w-3xl mx-auto text-center">
        <Sun size={20} className="text-[#6F4E37] mx-auto mb-4" />
        <p className="font-serif italic text-2xl md:text-3xl leading-snug text-[#2C2421]">
          “I hope you enjoy using your board as much as I enjoyed making it.”
        </p>
        <p className="mt-4 text-[#6F4E37]">— Hayden</p>
      </section>
    </div>
  );
}
